import type { RedlineDocumentContext } from './redline-types';
import {
  getIsRedlineEdit,
  getIsRedlineInsert,
  getIsRedlineNote,
  getIsRedlineRemove,
} from '../../../helpers/redlineCodeParser';
import type { ParsedRedlineItem } from '../../../helpers/redlineCodeParser';

/**
 * Derives the header's document context from the first edit, insert, or
 * removal in the list, whichever comes first. Edits anchor on their first
 * action's groupId; inserts and removals anchor on their own groupId.
 *
 * When the list contains only notes (a general user-facing message from the
 * agent, e.g. explaining that no edits were made), the first note still
 * supplies the documentId so the header can resolve the document name — but
 * with a null groupId, since a note's groupId exists only for stream dedup and
 * there's nothing in the document to navigate to. Errors contribute nothing —
 * they never landed as a suggestion in the document.
 */
export function getFirstRedlineContext(items: ParsedRedlineItem[]): RedlineDocumentContext | null {
  let noteDocumentId: string | null = null;
  for (const item of items) {
    if (getIsRedlineEdit(item)) {
      const groupId = item.actions[0]?.groupId;
      if (groupId) return { documentId: item.documentId, groupId };
    }
    if (getIsRedlineInsert(item) || getIsRedlineRemove(item)) {
      return { documentId: item.documentId, groupId: item.groupId };
    }
    if (getIsRedlineNote(item) && !noteDocumentId) {
      noteDocumentId = item.documentId;
    }
  }
  if (noteDocumentId) return { documentId: noteDocumentId, groupId: null };
  return null;
}
